/**
 * The ONLY way the advanced tier renders in the tenant SPA
 * (SPEC-TENANT-SURFACES UI-003 and RULE-011, overridden by D-TSG-26: the record
 * is DECISIONS.md, since #2014 retired the spec's rule text).
 *
 * EVERY BLOCK IN PLACE (D-TSG-26, owner ruling 2026-09-28 13:51:32Z, "Mindenhol
 * a helyén"). The server decides, per tenant, whether a registered block is
 * `visible` or `advanced`; a page renders every registered block ONCE, at its
 * natural position, through {@link SurfaceBlock}, inside ONE
 * {@link AdvancedGroupProvider}. There is no Advanced category and no
 * "Haladó beállítások" section anywhere. Per block:
 *
 * | resolved (me/surfaces)      | renders                                         |
 * |-----------------------------|-------------------------------------------------|
 * | unusable / absent           | nothing                                         |
 * | usable, state `visible`     | in place                                        |
 * | usable, state `advanced`    | in place, ONLY while the tier is open; else     |
 * |                             | nothing, and it leaves no space                 |
 *
 * (Before D-TSG-26 an `advanced` block moved into the page's one category; only
 * an `inPlaceOnly` row control stayed where it stood, D-TSG-15 (3). That is now
 * every block's behaviour, so `inPlaceOnly` changes nothing.)
 *
 * THE TIER IS OPEN when the tenant switch is on, the caller holds
 * `ui.use_advanced` AND the caller's own `advanced_mode` is on - or when the
 * server resolves the caller UNRESTRICTED (RULE-012(a), D-TSG-14). It can only
 * ever HIDE: nothing here makes a key usable that the server did not list
 * (RULE-003). While an answer is still loading the tier is closed.
 *
 * {@link AdvancedGroup} RENDERS NOTHING. Its props stay accepted so that no page
 * has to change in the same step; removing its mounts is a follow-up.
 *
 * CODE-SPLIT (RULE-011). The children of a {@link SurfaceBlock} SHOULD be a
 * `React.lazy` component; the block wraps them in `<Suspense fallback={null}>`
 * and mounts them only when it renders.
 *
 * MISUSE FAILS LOUDLY (UI-003): a key that is not registry-shaped, an
 * `advanced` block with no provider above it, and children handed to
 * {@link AdvancedGroup} (which renders none) log `console.error` and render
 * nothing for the offending element.
 *
 * THE STAFF SIGNAL (RULE-012, D-TSG-26 (2) and its 2026-09-30 amendment). For
 * a caller the server resolves `unrestricted`, a block the TENANT resolves
 * `hidden` or `advanced` carries the small {@link TenantHiddenBadge} icon laid
 * over the corner of the block itself, like a notification dot, so the layout
 * never moves - or, where that corner is a control, just beside the control, so
 * it never covers one; its tooltip and accessible name say what is marked
 * ("<label> - advanced for this tenant", see {@link BlockMarker}). For everyone
 * else the DOM does not change.
 *
 * @module components/shared/AdvancedGroup
 * @see SPEC-TENANT-SURFACES-UI-003 / RULE-011 / RULE-003 / RULE-012
 * @see DECISIONS.md D-TSG-26 (point 1: in place, no category)
 */
import {
  Children,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import type { MessageDescriptor } from "@lingui/core";
import { useSurfaceSet, type SurfaceKey } from "../../../surfaces";
import { TenantHiddenBadge, tenantMarkerOf } from "../TenantHiddenBadge";
import { usePermissionContext } from "../../../store/PermissionContext";
import { useUIPreferences } from "../../../api/uiPreferences";

/** The canonical id of the advanced-tier permission (SPEC-AUTHZ-PDP vocabulary, family `ui`). */
export const UI_USE_ADVANCED = "ui.use_advanced";

/** The registry's key syntax (RULE-017), the same pattern tools/check-surface-registry.py applies. */
const KEY_SYNTAX = /^(tenant|agent)(\.[a-z0-9]+(-[a-z0-9]+)*)+$/;

/** Is `key` shaped like a registry key? Shape only: existence is the registry gate's job (RULE-016). */
export function isSurfaceKeyShaped(key: string): boolean {
  return KEY_SYNTAX.test(key);
}

function misuse(message: string): void {
  console.error(`[AdvancedGroup] ${message} (SPEC-TENANT-SURFACES UI-003)`);
}

// ---------------------------------------------------------------------------
// The tier
// ---------------------------------------------------------------------------

/**
 * Whether the caller's tier is open (so `advanced` blocks render in place), and
 * three of the inputs it was built from (the fourth, `unrestricted`, is read
 * from `useSurfaceSet()`).
 */
export interface AdvancedTier {
  /**
   * The server resolved the caller unrestricted (RULE-012(a); D-TSG-14: always,
   * whatever the preference), OR tenant switch on AND `ui.use_advanced` held AND
   * `advanced_mode` on.
   */
  open: boolean;
  /** The tenant switch, as me/surfaces resolved it (false until it answers). */
  advancedEnabled: boolean;
  /** The caller holds `ui.use_advanced` (false while permissions load or on failure). */
  holdsPermission: boolean;
  /** The caller's presentation preference (false while loading, on failure, or when unread). */
  advancedMode: boolean;
}

/**
 * The tier for the current caller. Reads the preference ONLY for a tier holder
 * (switch on AND permission held), so a caller without the tier never reads it.
 * Also usable by the "Saját beállítások" page to decide whether to render the
 * toggle (`advancedEnabled && holdsPermission`).
 */
export function useAdvancedTier(): AdvancedTier {
  const surfaces = useSurfaceSet();
  const permissions = usePermissionContext();
  const holdsPermission = !permissions.loading && permissions.hasPermission(UI_USE_ADVANCED);
  const tierHolder = surfaces.advancedEnabled && holdsPermission;
  const preferences = useUIPreferences({ enabled: tierHolder });
  const advancedMode = tierHolder && preferences.status === "success" && preferences.advancedMode;
  const advancedEnabled = surfaces.advancedEnabled;
  // RULE-012(a): an UNRESTRICTED caller has every surface usable, its `advanced`
  // ones included. D-TSG-14 as RULED by the owner 2026-09-23 ("Staff mindig
  // látja"): the "sees everything" right OVERRIDES the Advanced-mode setting, so
  // the tier opens for such a caller whatever the preference says - a plain
  // verisa_staff user has no switch to turn on (TASK-TSURF-STAFF-ALWAYS-ADVANCED).
  // `unrestricted` is the SERVER's flag, read as-is (RULE-003), never derived here
  // from a permission, and only keys the server listed usable render at all.
  // For everyone else the preference still hides (SCEN-005).
  const open = surfaces.unrestricted || advancedMode;
  return useMemo(
    () => ({ open, advancedEnabled, holdsPermission, advancedMode }),
    [open, advancedEnabled, holdsPermission, advancedMode],
  );
}

/**
 * Whether a {@link SurfaceBlock} of `key` renders for this caller right now: the
 * key is registry-shaped and usable, and it is `visible` or the tier is open.
 * For a page that must know beforehand whether a block will fill its place - an
 * otherwise empty panel draws no frame (SPEC-FRONTEND RULE-042). It reads the
 * server's answer only and derives no tier of its own (RULE-003).
 */
export function useRendersInPlace(): (key: SurfaceKey) => boolean {
  const set = useSurfaceSet();
  const { open } = useAdvancedTier();
  return useCallback(
    (key: SurfaceKey) =>
      isSurfaceKeyShaped(key) && set.isUsable(key) && (set.surfaces[key]?.state !== "advanced" || open),
    [set, open],
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface Placement {
  tier: AdvancedTier;
}

const PlacementContext = createContext<Placement | null>(null);

/** Props of {@link AdvancedGroupProvider}. */
export interface AdvancedGroupProviderProps {
  /** The page, with its in-place {@link SurfaceBlock}s. */
  children: ReactNode;
}

/**
 * Wrap a page ONCE. It resolves the tier once for the page's
 * {@link SurfaceBlock}s. Renders no DOM of its own. Must sit inside the tenant
 * SPA's QueryClientProvider and PermissionProvider.
 */
export function AdvancedGroupProvider({ children }: AdvancedGroupProviderProps): ReactElement {
  const tier = useAdvancedTier();
  const value = useMemo<Placement>(() => ({ tier }), [tier]);
  return <PlacementContext.Provider value={value}>{children}</PlacementContext.Provider>;
}

// ---------------------------------------------------------------------------
// The staff marker on a block (RULE-012; D-TSG-26 amendment 2026-09-30)
// ---------------------------------------------------------------------------

/**
 * Elements that cannot carry the marker: void elements, and table, list and
 * select parts whose children must be cells, items or options.
 */
const NO_MARKER_HOST = new Set([
  "INPUT", "IMG", "BR", "HR", "TEXTAREA", "SELECT", "OPTION", "OPTGROUP",
  "TABLE", "THEAD", "TBODY", "TFOOT", "COLGROUP", "COL", "UL", "OL", "DL", "TEMPLATE",
]);

/** What counts as a control the marker sits ON (then it is no focus stop of its own). */
const CONTROL_SELECTOR = 'button, a[href], [role="button"], [role="menuitem"], [role="link"]';

/**
 * What the marker must never cover: every interactive control, measured by the
 * box it draws (a switch, checkbox or radio by its MUI root - its hidden input
 * is wider than what is drawn).
 */
const COVERABLE_CONTROL = [
  ".MuiSwitch-root", ".MuiCheckbox-root", ".MuiRadio-root", ".MuiSlider-root", ".MuiInputBase-root",
  "button", "a[href]", "select", "textarea", 'input:not([type="hidden"])',
  '[role="button"]', '[role="switch"]', '[role="checkbox"]', '[role="radio"]', '[role="combobox"]',
  '[role="slider"]', '[role="tab"]', '[role="link"]', '[role="menuitem"]',
].join(", ");
const DRAWN_ROOT = ".MuiSwitch-root, .MuiCheckbox-root, .MuiRadio-root";

/** The space between the marker and what it steps aside for, in px. */
const MARKER_GAP = 4;

/** Where the marker sits, in px from its host's padding box; null is the corner. */
type MarkerSpot = { top: number; right: number } | null;

interface Box2D {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

function overlaps(a: Box2D, b: Box2D): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

function boxOf(r: DOMRect | Box2D): Box2D {
  return { left: r.left, right: r.right, top: r.top, bottom: r.bottom };
}

/** Drawn at all (a collapsed section's content is not); true where the browser cannot tell. */
function shown(el: Element): boolean {
  return typeof el.checkVisibility !== "function" || el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
}

/**
 * What an icon draws: an SVG by its shapes (an MUI icon's 24 px box is a few px of
 * empty padding around the glyph), an image by its box; null when it draws nothing.
 */
function drawnBox(icon: Element): Box2D | null {
  const shapes = icon.tagName.toLowerCase() === "svg" ? [...icon.querySelectorAll("path, circle, rect, ellipse, polygon, polyline, line")] : [];
  const boxes = (shapes.length > 0 ? shapes : [icon])
    .map((el) => boxOf(el.getBoundingClientRect()))
    .filter((b) => b.right > b.left && b.bottom > b.top);
  if (boxes.length === 0) return null;
  return {
    left: Math.min(...boxes.map((b) => b.left)),
    right: Math.max(...boxes.map((b) => b.right)),
    top: Math.min(...boxes.map((b) => b.top)),
    bottom: Math.max(...boxes.map((b) => b.bottom)),
  };
}

/** A control at least this many marker widths wide is "wide": its empty padding may be covered, its content never. */
const WIDE_CONTROL = 4;

/**
 * What a marker must never cover, in `scope`: every small control whole (a switch,
 * checkbox, radio, slider or button), and of a wide one (a text field, a select, a
 * full-width clickable header) only what it draws - its icons and the text typed
 * in it; its other text comes from {@link textBoxes}. A control that contains the
 * host is left out: the marker is ON that one by the ruling (a kebab).
 */
function controlBoxes(scope: Element, host: HTMLElement, size: number): Box2D[] {
  const out: Box2D[] = [];
  for (const el of scope.querySelectorAll<HTMLElement>(COVERABLE_CONTROL)) {
    if (el.contains(host) || el.closest("[data-tenant-marker-corner]") || !shown(el)) continue;
    if (el.tagName === "INPUT" && el.closest(DRAWN_ROOT)) continue;
    const r = boxOf(el.getBoundingClientRect());
    if (r.right <= r.left || r.bottom <= r.top) continue;
    const wide = r.right - r.left >= WIDE_CONTROL * size && !el.matches(`${DRAWN_ROOT}, .MuiSlider-root`);
    if (!wide) {
      out.push(r);
      continue;
    }
    for (const icon of el.querySelectorAll("svg, img")) {
      const b = drawnBox(icon);
      if (b && shown(icon)) out.push(b);
    }
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      const typed = el.value || el.placeholder;
      if (!typed) continue;
      if (el instanceof HTMLTextAreaElement) {
        out.push(r);
        continue;
      }
      // An input's text is no DOM text: its width is estimated from the font size.
      const style = window.getComputedStyle(el);
      const font = parseFloat(style.fontSize) || 16;
      const start = r.left + (parseFloat(style.paddingLeft) || 0);
      out.push({ ...r, left: start, right: Math.min(r.right, start + typed.length * font * 0.6) });
    }
  }
  return out;
}

/**
 * The drawn lines of text in `scope` that reach into `near` (screen-reader-only
 * text is not drawn), split into the text of a control and all other text.
 */
function textBoxes(scope: Element, near: Box2D): { inControls: Box2D[]; other: Box2D[] } {
  const out = { inControls: [] as Box2D[], other: [] as Box2D[] };
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const el = node.parentElement;
    if (!el || !node.textContent?.trim() || el.closest("[data-tenant-marker-corner]") || !shown(el)) continue;
    const own = el.getBoundingClientRect();
    if (own.width <= 1 || own.height <= 1) continue;
    const range = document.createRange();
    if (typeof range.getClientRects !== "function") return out;
    range.selectNodeContents(node);
    const into = el.closest(COVERABLE_CONTROL) ? out.inControls : out.other;
    for (const r of range.getClientRects()) {
      if (r.width > 0 && r.height > 0 && overlaps(boxOf(r), near)) into.push(boxOf(r));
    }
  }
  return out;
}

/**
 * Whether no ancestor that clips its overflow cuts `spot`. Past an ancestor that
 * scrolls on an axis, the spot scrolls into view with the block, so no ancestor
 * further out clips it on that axis (a settings panel that scrolls inside an
 * `overflow: hidden` card).
 */
function unclipped(host: HTMLElement, spot: Box2D): boolean {
  let scrolledX = false;
  let scrolledY = false;
  for (let el: HTMLElement | null = host; el && el !== document.body; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    const x = style.overflowX || style.overflow;
    const y = style.overflowY || style.overflow;
    if (!x && !y) continue;
    const r = el.getBoundingClientRect();
    if (!scrolledX && x && x !== "visible" && (spot.left < r.left || spot.right > r.right)) return false;
    if (!scrolledY && (y === "hidden" || y === "clip") && (spot.top < r.top || spot.bottom > r.bottom)) return false;
    if (x === "auto" || x === "scroll") scrolledX = true;
    if (y === "auto" || y === "scroll") scrolledY = true;
  }
  return true;
}

/**
 * Where a `size` px marker on `host` covers nothing it must not (Tibor,
 * 2026-10-05: on a settings row the corner IS the switch, and the badge hid it;
 * "Javítsd ki minden oldalon ezeket."). In order: the corner; the corner as a
 * notification dot, half outside; just before what the corner covers, centred on
 * it, stepping left past anything further; just above its end; just below it;
 * just after the block. A spot must cover no control (see {@link controlBoxes})
 * and no text, and must not be cut off by a clipping ancestor (unless the corner
 * itself already is: then clipping decides nothing); when no spot is that clear, the first
 * one that covers no control will do; failing that, the corner. Measured in the
 * browser, so it holds on every page and every layout with no page opting in. An
 * empty measure (no layout, as in jsdom) is the corner.
 */
function markerSpot(host: HTMLElement, size: number): MarkerSpot {
  const box = boxOf(host.getBoundingClientRect());
  if (box.right <= box.left || box.bottom <= box.top) return null;
  const scope = host.parentElement ?? host;
  const corner = { left: box.right - size, right: box.right, top: box.top, bottom: box.top + size };
  const near = { left: -Infinity, right: Infinity, top: box.top - 2 * size - MARKER_GAP, bottom: box.bottom + size };
  const text = textBoxes(scope, near);
  const controls = [...controlBoxes(scope, host, size), ...text.inControls];
  if (![...controls, ...text.other].some((r) => overlaps(r, corner))) return null;

  const style = window.getComputedStyle(host);
  const borderTop = parseFloat(style.borderTopWidth) || 0;
  const borderRight = parseFloat(style.borderRightWidth) || 0;
  const toSpot = (s: Box2D): MarkerSpot => ({ top: s.top - box.top - borderTop, right: box.right - borderRight - s.right });
  const half = size / 2;
  // "Before" may reach past the host's own left edge into its parent: a block that is
  // just a switch (a FormControlLabel with no text) has no room of its own before it.
  const leftLimit = Math.min(box.left, scope.getBoundingClientRect().left);

  // Where the block itself is cut off (a page laid out wider than its frame), no spot
  // near it is more visible than another: clipping then decides nothing.
  const clipCounts = unclipped(host, corner);
  const shows = (s: Box2D) => !clipCounts || unclipped(host, s);
  for (const obstacles of [[...controls, ...text.other], controls]) {
    const free = (s: Box2D) => !obstacles.some((r) => overlaps(r, s)) && shows(s);
    if (free(corner)) return null;
    const dot = { left: box.right - half, right: box.right + half, top: box.top - half, bottom: box.top + half };
    if (free(dot)) return toSpot(dot);
    const hits = obstacles.filter((r) => overlaps(r, corner));
    const anchor = hits.reduce((a, b) => (b.right > a.right ? b : a), hits[0] ?? box);
    const middle = (anchor.top + anchor.bottom) / 2;
    let right = Math.min(...hits.map((r) => r.left)) - MARKER_GAP;
    for (let step = 0; step <= obstacles.length; step += 1) {
      const before = { left: right - size, right, top: middle - half, bottom: middle + half };
      if (before.left < leftLimit) break;
      const blockers = obstacles.filter((r) => overlaps(r, before));
      if (blockers.length === 0) {
        if (shows(before)) return toSpot(before);
        break;
      }
      right = Math.min(...blockers.map((r) => r.left)) - MARKER_GAP;
    }
    const above = { left: anchor.right - size, right: anchor.right, top: anchor.top - MARKER_GAP - size, bottom: anchor.top - MARKER_GAP };
    if (free(above)) return toSpot(above);
    const below = { left: anchor.right - size, right: anchor.right, top: anchor.bottom + MARKER_GAP, bottom: anchor.bottom + MARKER_GAP + size };
    if (free(below)) return toSpot(below);
    const after = { left: box.right + MARKER_GAP, right: box.right + MARKER_GAP + size, top: middle - half, bottom: middle + half };
    if (free(after)) return toSpot(after);
  }
  return null;
}

/**
 * The element the marker sits on: the block's first element in place, or null
 * when the block renders nothing in place (a closed dialog, a menu, a drawer) or
 * its first element cannot carry one. A table row carries it in its first cell.
 */
function markerHost(start: Element, end: Element): HTMLElement | null {
  for (let node = start.nextSibling; node && node !== end; node = node.nextSibling) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.tagName === "TR") return ((node as HTMLTableRowElement).cells[0] as HTMLElement | undefined) ?? null;
    return NO_MARKER_HOST.has(node.tagName) ? null : node;
  }
  return null;
}

/**
 * The staff marker ON the block it marks (D-TSG-26 amendment, Tibor 2026-09-30:
 * "Kis ikon-jelvény magán a jelölt vezérlőn ..., mint egy értesítési pötty. A
 * kártya elrendezése nem változik"). A small badge is laid over the top-right
 * corner of the block's first element - never a sibling, so no row, card or
 * list moves. (Before: a line of its own above the block, which in a
 * non-wrapping horizontal flex row became a free flex item and floated.) Where
 * the corner is a control - a settings row's switch, a card head's button - the
 * badge steps aside, still laid over the block (see {@link markerSpot}).
 *
 * Two `<template>` edges bracket the block - valid inside ANY parent, a table
 * row included - so the host is found in the real DOM, and re-found when the
 * parent's children change (a lazy block arriving). The badge is portalled into
 * the host; a host with `position: static` gets `position: relative` while the
 * badge is on it, restored when it leaves. On a control the badge is no focus
 * stop: the control names it in aria-describedby, and its tooltip opens on the
 * control's keyboard focus (TenantHiddenBadge, focusable={false}). Mounted only
 * for a marked block, i.e. only for an unrestricted caller.
 */
function BlockMarker({ surface, children }: { surface: SurfaceKey; children: ReactNode }): ReactElement {
  const startRef = useRef<HTMLTemplateElement>(null);
  const endRef = useRef<HTMLTemplateElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const badgeId = useId();

  useLayoutEffect(() => {
    const start = startRef.current;
    const end = endRef.current;
    if (!start || !end) return undefined;
    const measure = () => setHost(markerHost(start, end));
    measure();
    const parent = start.parentElement;
    if (!parent || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(measure);
    observer.observe(parent, { childList: true });
    return () => observer.disconnect();
  }, []);

  const control = host?.closest<HTMLElement>(CONTROL_SELECTOR) ?? null;
  // TenantHiddenBadge's own size: 18 px on a control, 24 px where it is a focus stop.
  const size = control ? 18 : 24;
  const [spot, setSpot] = useState<MarkerSpot>(null);

  useLayoutEffect(() => {
    if (!host) return undefined;
    const measure = () =>
      setSpot((prev) => {
        const next = markerSpot(host, size);
        return prev?.top === next?.top && prev?.right === next?.right ? prev : next;
      });
    // Measure again whenever what lies under the marker may have changed, at most
    // once a frame: the host resized, came into view (a collapsed section opened,
    // which resizes nothing), a transition ended (an opening section clips until it
    // has opened), or a control beside it changed.
    let frame = 0;
    const later = () => {
      if (frame) return;
      frame = window.setTimeout(() => {
        frame = 0;
        measure();
      }, 16);
    };
    measure();
    window.addEventListener("resize", later);
    document.addEventListener("transitionend", later, true);
    document.addEventListener("animationend", later, true);
    const resized = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(later);
    resized?.observe(host);
    const seen = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(later, { threshold: [0, 0.5, 1] });
    seen?.observe(host);
    const scope = host.parentElement;
    const changed = scope && typeof MutationObserver !== "undefined" ? new MutationObserver(later) : null;
    if (scope) changed?.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "hidden", "aria-expanded"] });
    return () => {
      window.clearTimeout(frame);
      window.removeEventListener("resize", later);
      document.removeEventListener("transitionend", later, true);
      document.removeEventListener("animationend", later, true);
      resized?.disconnect();
      seen?.disconnect();
      changed?.disconnect();
    };
  }, [host, size]);

  useLayoutEffect(() => {
    if (!host) return undefined;
    const restore: Array<() => void> = [];
    const position = window.getComputedStyle(host).position;
    if (!position || position === "static") {
      const previous = host.style.position;
      host.style.position = "relative";
      restore.push(() => {
        host.style.position = previous;
      });
    }
    if (control) {
      const previous = control.getAttribute("aria-describedby");
      control.setAttribute("aria-describedby", previous ? `${previous} ${badgeId}` : badgeId);
      restore.push(() => {
        if (previous === null) control.removeAttribute("aria-describedby");
        else control.setAttribute("aria-describedby", previous);
      });
    }
    return () => restore.forEach((undo) => undo());
  }, [host, control, badgeId]);

  return (
    <>
      <template ref={startRef} />
      {children}
      <template ref={endRef} />
      {host &&
        createPortal(
          <Box
            component="span"
            data-tenant-marker-corner=""
            sx={{ position: "absolute", zIndex: 1, lineHeight: 0 }}
            style={spot ? { top: spot.top, right: spot.right } : { top: 0, right: 0 }}
          >
            <TenantHiddenBadge
              surface={surface}
              named
              variant="corner"
              focusable={!control}
              descriptionId={control ? badgeId : undefined}
            />
          </Box>,
          host,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// SurfaceBlock
// ---------------------------------------------------------------------------

/** Props of {@link SurfaceBlock}. */
export interface SurfaceBlockProps {
  /**
   * The block's registry key (RULE-016/017), as a string LITERAL so
   * tools/check-surface-registry.py can check it - e.g. the key
   * `agent.settings.ai.temperature`, written as a JSX string literal. (Not
   * spelled out here: the checker reads comments too, and would count this
   * line as a mount.)
   */
  surface: SurfaceKey;
  /**
   * The block itself - preferably a `React.lazy` component element, so its code
   * loads only when the block renders (RULE-011 code-split). Wrapped in
   * `<Suspense fallback={null}>`.
   */
  children: ReactNode;
  /**
   * Kept for the pages that still pass it: since D-TSG-26 every block stays
   * where it stands, so it changes nothing (it marked a row control that must
   * not move, D-TSG-15 (3)).
   */
  inPlaceOnly?: boolean;
}

/**
 * One registered in-page block, placed where it belongs. The server's answer
 * decides whether it renders there (see the module table): an `advanced` block
 * only while the tier is open, and otherwise nothing, leaving no space. A block
 * that resolves `advanced` needs an {@link AdvancedGroupProvider} above it.
 */
export function SurfaceBlock({ surface, children }: SurfaceBlockProps): ReactElement | null {
  const set = useSurfaceSet();
  const placement = useContext(PlacementContext);
  const shaped = isSurfaceKeyShaped(surface);
  const usable = shaped && set.isUsable(surface);
  const advanced = usable && set.surfaces[surface]?.state === "advanced";

  useEffect(() => {
    if (!shaped) misuse(`SurfaceBlock surface=${JSON.stringify(surface)} is not a registry key`);
    else if (advanced && !placement) {
      misuse(`SurfaceBlock ${surface} resolved advanced but has no <AdvancedGroupProvider> above it`);
    }
  }, [shaped, advanced, placement, surface]);

  if (!usable) return null;
  if (advanced && (!placement || !placement.tier.open)) return null;
  // RULE-012: an unrestricted caller sees what the tenant hid or tiered, with the
  // icon that says so. Inside the Suspense boundary, so the icon is measured once
  // the block itself has committed (see BlockMarker).
  const marked = tenantMarkerOf(set, surface) !== null;
  return (
    <Suspense fallback={null}>{marked ? <BlockMarker surface={surface}>{children}</BlockMarker> : children}</Suspense>
  );
}

// ---------------------------------------------------------------------------
// AdvancedGroup
// ---------------------------------------------------------------------------

/** Props of {@link AdvancedGroup}: accepted, and unused since D-TSG-26. */
export interface AdvancedGroupProps {
  /** The keys the page used to contribute to its category; still read by the registry check. */
  surfaceKeys: readonly SurfaceKey[];
  /** Never rendered since D-TSG-26: passing any is reported as a misuse, never dropped silently. */
  children?: ReactNode;
  /** Unused since D-TSG-26 (RULE-011 SUB-GROUPS is superseded). */
  groups?: readonly AdvancedSubGroup[];
  /** Unused since D-TSG-26. */
  headingComponent?: "h2" | "h3";
  /** Unused since D-TSG-26. */
  "data-testid"?: string;
}

/** A former sub-group declaration (D-TSG-19, superseded by D-TSG-26); accepted, unused. */
export interface AdvancedSubGroup {
  section: SurfaceKey;
  title: MessageDescriptor;
}

/**
 * Renders NOTHING (D-TSG-26: there is no Advanced category). Kept so that the
 * pages that mount it compile unchanged; a key that is not registry-shaped is
 * still reported, as before, and so is any child - it would otherwise vanish
 * without a trace (a block belongs in place, through {@link SurfaceBlock}).
 */
export function AdvancedGroup({ surfaceKeys, groups, children }: AdvancedGroupProps): null {
  const malformed = [...surfaceKeys, ...(groups ?? []).map((g) => g.section)].filter((k) => !isSurfaceKeyShaped(k));
  const malformedSignature = malformed.join("\n");
  const hasChildren = Children.count(children) > 0;
  useEffect(() => {
    if (malformedSignature !== "") {
      misuse(`<AdvancedGroup> holds non-registry keys: ${malformedSignature.split("\n").join(", ")}`);
    }
  }, [malformedSignature]);
  useEffect(() => {
    if (hasChildren) {
      misuse("<AdvancedGroup> renders nothing since D-TSG-26; its children are not rendered - render them in place");
    }
  }, [hasChildren]);
  return null;
}
